// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type {
  AppConfig,
  AppConfigUpdateRequest,
  AppIntegration,
  OidcAppIntegration,
  OidcDiscoveryResponse,
} from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button, Modal } from '@imprivata-cloud/components';
import { ConfigProvider, Form, type FormInstance } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DesktopLockSection from '../components/DesktopLockSection';
import HyperdriveConfigForm, {
  type HyperdriveFormValues,
} from '../components/HyperdriveConfigForm';
import type { OidcCommitOutcome, OidcConfigSectionHandle } from '../components/OidcConfigSection';
import OidcConfigSection from '../components/OidcConfigSection';
import { useAppConfigMutation } from '../hooks/useAppConfig';
import type { CreateOidcResult } from '../hooks/useOidcAppConfig';
import { NO_WAVE } from '../constants';
import './HyperdriveModal.less';

const APP_ID = 'epic-hyperdrive';
const TRANSLATION_ROOT = 'apps.app-modal';

// Verbatim port of the legacy `isAppConfigured` helper from saas-admin-ui's
// AppModal. Drives Add vs. Edit titling and action label.
export const isAppConfigured = (
  ai: AppIntegration | undefined,
  ac: AppConfig | undefined,
): boolean => ai?.idpIssuer != null && ac?.appUsernameAdMatch != null;

interface HyperdriveModalProps {
  appIntegration: AppIntegration;
  appConfig: AppConfig | undefined;
  isAppConfigLoading: boolean;
  redirectUri: string | undefined;
  oidcConfig: OidcAppIntegration | undefined;
  isOidcLoading: boolean;
  discoveryData: OidcDiscoveryResponse | undefined;
  createOidcConfig: (uris: string[], displayName: string) => Promise<CreateOidcResult>;
  resetCreateOidcConfig: () => void;
  updateOidcConfig: (clientId: string, uris: string[]) => Promise<unknown>;
  deleteOidcConfig: (clientId: string) => Promise<unknown>;
  onClose: () => void;
  onSaved: () => void;
  onOidcCreated: () => void;
}

const HyperdriveModal: React.FC<HyperdriveModalProps> = ({
  appIntegration,
  appConfig,
  isAppConfigLoading,
  redirectUri,
  oidcConfig,
  isOidcLoading,
  discoveryData,
  createOidcConfig,
  resetCreateOidcConfig,
  updateOidcConfig,
  deleteOidcConfig,
  onClose,
  onSaved,
  onOidcCreated,
}) => {
  const { t } = useTranslation();
  const { tenantId } = useSession();
  const [form] = Form.useForm<HyperdriveFormValues>();
  const appUsernameAdMatch = Form.useWatch('appUsernameAdMatch', form as FormInstance);
  const { emitError, clearErrorNotifications } = useErrorContext();
  const { mutate, isPending } = useAppConfigMutation(APP_ID);
  const oidcRef = useRef<OidcConfigSectionHandle>(null);
  const [showOidcSuccess, setShowOidcSuccess] = useState(false);
  const [isOidcReady, setIsOidcReady] = useState(true);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isOidcDirty, setIsOidcDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const configured = isAppConfigured(appIntegration, appConfig);
  const titleKey = configured ? 'title-edit' : 'title-add';
  const actionKey = configured ? 'action-save' : 'action-add';

  // Legacy parity: pre-fill the mapping dropdown with
  // `false` when no value has been saved yet, matching saas-admin-ui's
  // DEFAULT_EPIC_USERNAME_MATCHES_AD behavior.
  // Seed the lock dropdowns from saved values; leave
  // each sub-key undefined when not saved so the placeholder shows.
  const initialValues: HyperdriveFormValues = {
    appUsernameAdMatch: appConfig?.appUsernameAdMatch ?? false,
    interconnectUrl: appConfig?.interconnectBaseUrls?.[0] ?? '',
    epicAppLockConfig: {
      sharedWorkstation: appConfig?.epicAppLockConfig?.sharedWorkstation,
      privateWorkstation: appConfig?.epicAppLockConfig?.privateWorkstation,
    },
  };

  const hasChanges = !configured || isFormTouched || isOidcDirty;

  const onFinish = (values: HyperdriveFormValues) => {
    clearErrorNotifications();
    if (oidcRef.current && !oidcRef.current.validate()) {
      return;
    }
    const trimmed = values.interconnectUrl?.trim();
    const interconnectBaseUrls = trimmed ? [trimmed] : [];
    const rawLock = values.epicAppLockConfig ?? {};
    const epicAppLockConfig = Object.fromEntries(
      Object.entries(rawLock).filter(([, v]) => v !== undefined),
    );
    const req: AppConfigUpdateRequest = {
      applicationId: APP_ID,
      appConfig: {
        samlEnabled: true,
        captureMaxRetries: appConfig?.captureMaxRetries ?? 3,
        appUsernameAdMatch: values.appUsernameAdMatch,
        interconnectBaseUrls,
        epicAppLockConfig,
      },
    };
    setIsSaving(true);
    mutate(req, {
      onSuccess: async () => {
        try {
          const outcome: OidcCommitOutcome = (await oidcRef.current?.commit()) ?? 'noop';
          if (outcome === 'created') {
            resetCreateOidcConfig();
            onOidcCreated();
            // Modal stays open — OidcConfigSection shows the success/credentials state.
            // Done button inside OidcConfigSection calls onCloseModal to close.
          } else if (outcome === 'updated' || outcome === 'noop') {
            onSaved();
            onClose();
          }
        } catch (e) {
          emitError(new AppError(e as ApiError));
        } finally {
          setIsSaving(false);
        }
      },
      onError: (e: ApiError) => {
        setIsSaving(false);
        emitError(new AppError(e));
      },
    });
  };

  const handleCancel = () => {
    if (isSaving) {
      return;
    }
    // Route through the close guard only when a secret is currently displayed.
    if (showOidcSuccess) {
      oidcRef.current?.requestClose(onClose);
    } else {
      onClose();
    }
  };

  const modalTitle = showOidcSuccess
    ? t(`${TRANSLATION_ROOT}.title-success`, { appDisplayName: appIntegration.appDisplayName })
    : t(`${TRANSLATION_ROOT}.${titleKey}`, { appDisplayName: appIntegration.appDisplayName });

  return (
    <ConfigProvider wave={NO_WAVE}>
      <Modal
        open
        // Scopes the modal-only stylesheet (footer
        // alignment, SAML row layout, section-title rhythm) to this
        // modal. Matches the `.user-modal` pattern in `UserModal.tsx`.
        className="hyperdrive-modal"
        title={modalTitle}
        width={650}
        onCancel={handleCancel}
        footer={null}
        maskClosable
        destroyOnHidden
      >
        {isAppConfigLoading ? (
          <div>{t(`${TRANSLATION_ROOT}.loading`)}</div>
        ) : (
          // The lock Form.Items sit after OidcConfigSection, so the form spans
          // it; component={false} keeps OIDC buttons from native-submitting.
          <Form
            form={form}
            component={false}
            layout="vertical"
            initialValues={initialValues}
            onFinish={onFinish}
            onValuesChange={() => setIsFormTouched(true)}
          >
            {!showOidcSuccess && (
              <HyperdriveConfigForm
                appDisplayName={appIntegration.appDisplayName}
                isEdit={configured}
                idpIssuer={appIntegration.idpIssuer}
                redirectUri={redirectUri ?? ''}
                idpCertificate={appIntegration.idpCertificates[0]?.idpCertificate ?? ''}
                tenantId={tenantId ?? ''}
              />
            )}
            <OidcConfigSection
              ref={oidcRef}
              tenantId={tenantId ?? ''}
              appDisplayName={appIntegration.appDisplayName}
              discoveryData={discoveryData}
              oidcConfig={oidcConfig}
              isOidcLoading={isOidcLoading}
              isCollapsedByDefault={configured && !!oidcConfig}
              showFirstSignInBanner={appUsernameAdMatch === false}
              onCloseModal={onClose}
              onOidcSuccess={() => setShowOidcSuccess(true)}
              onReadyChange={setIsOidcReady}
              onDirtyChange={setIsOidcDirty}
              createOidcConfig={createOidcConfig}
              updateOidcConfig={updateOidcConfig}
              deleteOidcConfig={deleteOidcConfig}
            />
            {!showOidcSuccess && <DesktopLockSection />}
            {!showOidcSuccess && (
              <div className="form-footer-right">
                <Button onClick={handleCancel} label={t(`${TRANSLATION_ROOT}.action-cancel`)} />
                <Button
                  type="primary"
                  onClick={() => form.submit()}
                  label={t(`${TRANSLATION_ROOT}.${actionKey}`)}
                  disabled={isPending || isSaving || !isOidcReady || !hasChanges}
                />
              </div>
            )}
          </Form>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default HyperdriveModal;
